import * as Yup from 'yup';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import { Button, Link } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import LoadingButton from '@mui/lab/LoadingButton';

import { RouterLink } from 'src/routes/components';

import { useSnackbar } from 'src/components/snackbar';
import FormProvider, { RHFTextField } from 'src/components/hook-form';

import { IPackagingProductItem } from 'src/types/packaging-product';

import { paths } from '../../routes/paths';
import { useAppDispatcher } from '../../redux/store';
import {
  createPackagingProduct,
  TCreatePackagingProduct,
  updatePackagingProduct,
} from '../../redux/slices/packaging-product';

type Props = {
  currentProduct?: IPackagingProductItem;
};

export default function PackagingProductNewEditForm({ currentProduct }: Props) {
  const dispatch = useAppDispatcher();

  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const NewProductSchema = Yup.object().shape({
    productName: Yup.string().required('Product name is required'),
    productCode: Yup.string().optional().nullable().default(null),
  });

  const defaultValues = useMemo(
    () => ({
      productName: currentProduct?.productName || '',
      productCode: currentProduct?.productCode || null,
    }),
    [currentProduct]
  );

  const methods = useForm({
    resolver: yupResolver(NewProductSchema),
    defaultValues,
    mode: 'onChange',
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
    trigger,
  } = methods;

  const onSubmit = handleSubmit(async (data: TCreatePackagingProduct) => {
    try {
      if (currentProduct) {
        await dispatch(
          updatePackagingProduct({
            productId: currentProduct._id,
            product: data,
          })
        ).unwrap();

        enqueueSnackbar('Product updated');
      } else {
        await dispatch(
          createPackagingProduct({
            product: data,
          })
        ).unwrap();

        enqueueSnackbar('Product created');
      }

      navigate(paths.dashboard.packagingProduct.root);
      reset();
    } catch (e) {
      if (typeof e === 'string') {
        enqueueSnackbar(e, {
          variant: 'error',
        });
      } else {
        enqueueSnackbar(e?.message, {
          variant: 'error',
        });
      }
    }
  });

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Grid container spacing={3}>
        <Grid xs={12}>
          <Card sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid xs={12} md={6}>
                <RHFTextField required name="productName" label="Product Name" />
              </Grid>

              <Grid xs={12} md={6}>
                <RHFTextField name="productCode" label="Product Code" />
              </Grid>
            </Grid>

            <Stack alignItems="flex-end" sx={{ mt: 3 }}>
              <Stack direction="row" gap={2}>
                <Link component={RouterLink} href={paths.dashboard.packagingProduct.root}>
                  <Button variant="outlined" color="inherit">
                    Cancel
                  </Button>
                </Link>

                <LoadingButton
                  type="submit"
                  variant="contained"
                  loading={isSubmitting}
                  onClick={() => trigger()}
                >
                  {!currentProduct ? 'Create Product' : 'Save Changes'}
                </LoadingButton>
              </Stack>
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </FormProvider>
  );
}
